import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Checkbox,
    Collapse,
    Divider,
    Form,
    Input,
    InputNumber,
    Select,
    Space,
    Typography
} from "antd";
import AuthorSelect from "./AuthorSelect";
import PolicyModelModal from "./PolicyModelModal";
import BorrowedContentPriceBreakdown from "./BorrowedContentPriceBreakdown";
import "./ModuleConfigForm.scss";

const { Text } = Typography;

const formItemLayout = {
    labelCol: {
        xs: { span: 24 },
        sm: { span: 8 },
        xl: { span: 8 }
    },
    wrapperCol: {
        xs: { span: 24 },
        sm: { span: 16 },
        xl: { span: 8 }
    }
};

const ModuleConfigForm = ({
    module,
    moduleIdentity,
    editPermission = "Default",
    policyModel,
    authorsDict,
    currentUser,
    collectionCategories,
    fetchCreatorList,
    onSubmit,
    onCancel,
    submitLabel = "Save",
    showActions = true,
    autoSave = false,
    onModuleChange,
    disableSubmit = false
}) => {
    const [form] = Form.useForm();
    const [useDefaultPrice, setUseDefaultPrice] = useState(
        module?.isDefaultPrice ?? true
    );
    const [selectedCategoryId, setSelectedCategoryId] = useState(
        module?.category || null
    );
    const [policyModelVisible, setPolicyModelVisible] = useState(false);
    const [borrowedPriceBreakdownVisible, setBorrowedPriceBreakdownVisible] =
        useState(false);
    const lastIdentityRef = React.useRef(null);
    const lastNameRef = React.useRef(null);

    const defaultPrice = policyModel?.platformDefaultPrice ?? 200;
    const isReadOnly = editPermission === "None";
    const canEditAll = editPermission === "Default";
    const canEditName = editPermission === "Default" || editPermission === "NameOnly";

    const identity =
        moduleIdentity ?? module?.key ?? module?.id ?? "default-module";

    useEffect(() => {
        if (lastIdentityRef.current === identity) return;
        lastIdentityRef.current = identity;
        form.setFieldsValue(module);
        setSelectedCategoryId(module?.category || null);
        setUseDefaultPrice(module?.isDefaultPrice ?? true);
        lastNameRef.current = module?.name ?? "";
    }, [form, identity, module]);

    useEffect(() => {
        const nextName = module?.name ?? "";
        if (lastNameRef.current === nextName) return;
        lastNameRef.current = nextName;
        form.setFieldsValue({ name: nextName });
    }, [form, module?.name]);

    const categoryOptions = useMemo(
        () => [
            { label: "Select a category", value: null, subCategories: [] },
            ...(collectionCategories || []).map((c) => ({
                label: c.name,
                value: c.id,
                subCategories: c.subCategories || []
            }))
        ],
        [collectionCategories]
    );

    const subCategoryOptions = useMemo(() => {
        const cat = categoryOptions.find((c) => c.value === selectedCategoryId);
        return [
            { label: "Select a subcategory", value: null },
            ...((cat?.subCategories || []).map((sc) => ({
                label: sc.name,
                value: sc.id
            })) || [])
        ];
    }, [categoryOptions, selectedCategoryId]);

    const buildUpdatedModule = (values, useDefaultOverride = useDefaultPrice) => {
        const safeValues = values || {};
        const primaryAuthorUsername = safeValues.primaryAuthorUsername;
        const authorData =
            (primaryAuthorUsername &&
                authorsDict?.[primaryAuthorUsername]) ||
            currentUser ||
            {};
        const coAuthorUsernames = safeValues.coAuthorUsernames || [];
        const coAuthors = coAuthorUsernames.map(
            (username) => authorsDict?.[username] || currentUser || {}
        );

        const updated = {
            ...module,
            ...safeValues,
            primaryAuthorUsername: authorData.username,
            primaryAuthor: authorData,
            coAuthors,
            coAuthorUsernames
        };

        if (useDefaultOverride) {
            updated.isDefaultPrice = true;
            updated.customPrice = null;
        } else {
            updated.isDefaultPrice = false;
            updated.customPrice = safeValues.customPrice ?? 0;
        }

        return updated;
    };

    const handleValuesChange = (changedValues, allValues) => {
        if (Object.prototype.hasOwnProperty.call(changedValues, "category")) {
            setSelectedCategoryId(changedValues.category);
            form.setFieldsValue({ subCategory: undefined });
        }
        if (autoSave && onModuleChange) {
            onModuleChange(buildUpdatedModule(allValues));
        }
    };

    const handleOnCheck = () => {
        const nextUseDefault = !useDefaultPrice;
        setUseDefaultPrice(nextUseDefault);
        form.resetFields(["customPrice"]);
        if (autoSave && onModuleChange) {
            onModuleChange(buildUpdatedModule(form.getFieldsValue(), nextUseDefault));
        }
    };

    const calculateTotalPrice = () => {
        if (useDefaultPrice) return defaultPrice;
        const customPrice = form.getFieldValue("customPrice");
        return customPrice ? customPrice : 0;
    };

    const handleFinish = (values) => {
        if (!onSubmit) return;
        onSubmit(buildUpdatedModule(values));
    };

    const category =
        collectionCategories?.find((c) => c.id === module?.category) || null;
    const subCategory =
        category?.subCategories?.find((sc) => sc.id === module?.subCategory) ||
        null;
    const totalPrice =
        module?.price ??
        (module?.isDefaultPrice ? defaultPrice : module?.customPrice ?? 0);

    const renderInfoOnly = (showName) => (
        <div className="module-config-info">
            {showName && (
                <div className="module-config-info-row">
                    <Text>Module Name</Text>
                    <Text strong>{module?.name || "Untitled Module"}</Text>
                </div>
            )}
            <div className="module-config-info-row">
                <Text>Category</Text>
                <Text>{category?.name || "Uncategorized"}</Text>
            </div>
            <div className="module-config-info-row">
                <Text>Sub Category</Text>
                <Text>{subCategory?.name || "None"}</Text>
            </div>
            <div className="module-config-info-row">
                <Text>Module Sharing</Text>
                <Text>{module?.moduleSharing ? "Enabled" : "Disabled"}</Text>
            </div>
            <div className="module-config-info-row">
                <Text>Author</Text>
                <Text>{module?.primaryAuthorUsername || "Unknown"}</Text>
            </div>
            <div className="module-config-info-row">
                <Text>Co-Authors</Text>
                <Text>
                    {(module?.coAuthorUsernames || []).length > 0
                        ? module.coAuthorUsernames.join(", ")
                        : "None"}
                </Text>
            </div>
            <div className="module-config-info-row">
                <Text>Price of your content</Text>
                <Text>{totalPrice} cupoints</Text>
            </div>
        </div>
    );

    if (isReadOnly) {
        return renderInfoOnly(true);
    }

    return (
        <Form
            {...formItemLayout}
            form={form}
            className="flex flex-col full-height"
            name="module-config-form"
            layout="vertical"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            initialValues={module}
            onFinish={handleFinish}
            onValuesChange={handleValuesChange}
            autoComplete="off"
        >
            <div className="flex flex-col full-height pr-10">
                {canEditName ? (
                    <Form.Item
                        label="Module Name"
                        className="w-100 module-required-right"
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: "Please give your module a name!"
                            }
                        ]}
                    >
                        <Input size="large" placeholder="Module Name" />
                    </Form.Item>
                ) : (
                    <div className="module-config-info-row module-config-name-row module-required-right">
                        <div className="ant-form-item-label">
                            <label className="module-config-required-label ant-form-item-required">
                                Module Name
                            </label>
                        </div>
                        <Form.Item
                            name="name"
                            wrapperCol={{ span: 24 }}
                            rules={[
                                {
                                    required: true,
                                    message: "Please give your module a name!"
                                }
                            ]}
                            className="w-100 module-config-name-item"
                        >
                            <Input size="large" placeholder="Module Name" className="w-100"/>
                        </Form.Item>
                    </div>
                )}

                {!canEditAll ? (
                    renderInfoOnly(false)
                ) : (
                    <>
                        <Form.Item label="Category" className="w-100" name="category">
                            <Select
                                size="large"
                                placeholder="Select a category"
                                options={categoryOptions}
                                value={selectedCategoryId || undefined}
                                onChange={(value) => {
                                    setSelectedCategoryId(value);
                                    form.setFieldsValue({
                                        subCategory: undefined,
                                        category: value
                                    });
                                }}
                            />
                        </Form.Item>

                        {subCategoryOptions.length > 0 &&
                            selectedCategoryId !== null && (
                                <Form.Item
                                    label="Sub Category"
                                    className="w-100"
                                    name="subCategory"
                                >
                                    <Select
                                        size="large"
                                        placeholder="Select a subcategory"
                                        options={subCategoryOptions}
                                    />
                                </Form.Item>
                            )}

                        <Form.Item
                            name="moduleSharing"
                            valuePropName="checked"
                            className="w-100"
                            label="Module Sharing"
                            extra="If enabled, other creators can include this module in their courses."
                        >
                            <Checkbox>
                                Let other creators reuse this module in their courses
                            </Checkbox>
                        </Form.Item>

                        <Collapse>
                            <Collapse.Panel header="Not the author?" key="1" forceRender>
                                <Form.Item
                                    label="Author"
                                    name={["primaryAuthorUsername"]}
                                    fieldKey={["primaryAuthorUsername"]}
                                    tooltip="If you are not the primary author, search for the user's account by username or name. Only this user will get paid for any purchases. If you leave this blank, the primary author will be set to you."
                                >
                                    <AuthorSelect
                                        placeholder={
                                            "Select or search for a user by username (Or leave blank to default to yourself)"
                                        }
                                        fetchOptions={fetchCreatorList}
                                        mode={null}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="Co-Authors"
                                    name={["coAuthorUsernames"]}
                                    fieldKey={["coAuthorUsernames"]}
                                    tooltip="Used mainly for credit purposes only."
                                >
                                    <AuthorSelect
                                        placeholder={
                                            "Select or search for a user by username"
                                        }
                                        fetchOptions={fetchCreatorList}
                                        mode={"multiple"}
                                    />
                                </Form.Item>
                            </Collapse.Panel>
                        </Collapse>
                        <br />
                        <div className="price-breakdown">
                            <div className="price-breakdown-row">
                                <div>
                                    <Text>Price of your content</Text>
                                    <br />
                                    <Text
                                        type="secondary"
                                        className="price-breakdown-hint"
                                        onClick={() => setPolicyModelVisible(true)}
                                    >
                                        How should I price my module?
                                    </Text>
                                    <PolicyModelModal
                                        policyModel={policyModel}
                                        visible={policyModelVisible}
                                        setVisible={setPolicyModelVisible}
                                    />
                                </div>
                                <Space align="baseline">
                                    <Checkbox
                                        checked={useDefaultPrice}
                                        onChange={handleOnCheck}
                                    >
                                        Use Default Price
                                    </Checkbox>
                                    {useDefaultPrice ? (
                                        <InputNumber
                                            style={{ marginBottom: "16px" }}
                                            value={defaultPrice}
                                            disabled
                                        />
                                    ) : (
                                        <Form.Item name="customPrice">
                                            <InputNumber min={0} />
                                        </Form.Item>
                                    )}
                                </Space>
                            </div>
                            <div className="price-breakdown-row">
                                <div>
                                    <Text>Price of borrowed content</Text>
                                    <br />
                                    <Text
                                        type="secondary"
                                        className="price-breakdown-hint"
                                        onClick={() =>
                                            setBorrowedPriceBreakdownVisible(true)
                                        }
                                    >
                                        How is this calculated?
                                    </Text>
                                    <BorrowedContentPriceBreakdown
                                        module={module}
                                        setVisible={setBorrowedPriceBreakdownVisible}
                                        visible={borrowedPriceBreakdownVisible}
                                    />
                                </div>
                                <Text>0 Cupoints</Text>
                            </div>
                            <Divider style={{ margin: "10px 0px" }} />
                            <div className="price-breakdown-row">
                                <Text>Total Price</Text>
                                <Text>{calculateTotalPrice()}cupoints</Text>
                            </div>
                        </div>
                    </>
                )}
            </div>
            {showActions && (
                <div className="mt-auto">
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            disabled={disableSubmit}
                        >
                            {submitLabel}
                        </Button>
                    </Space>
                </div>
            )}
        </Form>
    );
};

export default ModuleConfigForm;
